import { ICustomerAddressBody } from "@/api_framework/api_modals/Addresses";
import { ICustomerPetCreationBody } from "@/api_framework/api_modals/Pets";
import {
  ICustomerCreationRequestBody,
  ICustomerUpdationRequestBody,
  IExotelInformationData,
} from "@/api_framework/api_modals/Users";

import { useAddAddressAPI } from "@/app/hooks/api_hooks/customer_profile/useAddAddressAPI";
import { useCustomerCreationAPI } from "@/app/hooks/api_hooks/customer_profile/useCustomerCreationAPI";
import { useCustomerDetailsAPI } from "@/app/hooks/api_hooks/customer_profile/useCustomerDetailsAPI";
import { useCustomerPetCreationAPI } from "@/app/hooks/api_hooks/customer_profile/useCustomerPetCreationAPI";
import { useCustomerPetListAPI } from "@/app/hooks/api_hooks/customer_profile/useCustomerPetListAPI";
import { useCustomerPetUpdationAPI } from "@/app/hooks/api_hooks/customer_profile/useCustomerPetUpdateAPI";
import { useCustomerUpdationAPI } from "@/app/hooks/api_hooks/customer_profile/useCustomerUpdationAPI";
import { useModalActions } from "@/app/hooks/ui_hooks/useModalActions";
import useRouteInfo from "@/app/hooks/useRouteInfo";
import { useRouter } from "@/app/hooks/useRouter";
import { useStringConstants } from "@/app/hooks/useStringConstants";
import { useUtils } from "@/app/hooks/useUtils";
import { ROUTES } from "@/app/routes";
import { useAppDispatch, useAppSelector } from "@/app_redux/hooks/root_hook";
import {
  ICustomerProfileState,
  setCustomerId,
  setMissingDetailsRedirect,
  setPetId,
} from "@/app_redux/reducers/slices/auth/customer_profile_slice";
import { ISlotsState } from "@/app_redux/reducers/slices/auth/slots_slice";
import { useEffect, useState } from "react";
import { FieldErrors, SubmitHandler, useFieldArray, useForm } from "react-hook-form";
import { useNavigate } from "react-router";
import { IAddCustomerFormsProps } from "../../customer_profile_interface";
import CustomerInfoFormComponent from "../CustomerInfoForm";
import FooterAddCustomerComponent from "../Footer";
import PetInfoFormComponent from "../PetInfoForm";
import AICustomerProfileCard from "../../CustomerDetails/CustomerInformation/AICustomerProfileCard";
import AIPetProfileCard from "../../CustomerDetails/CustomerInformation/AIPetProfileCard";
import {
  IAddCustomerCustomerFormFields,
  IAddCustomerFormsFormFields,
  IAddCustomerPetFormFields,
  NavTo,
} from "./interface";
import toast from "react-hot-toast";

const addressFieldDefaultValues = {
  address_1: null,
  address_2: "",
  city: null,
  label: "",
  pincode: null,
  state: null,
  latitude: null,
  longitude: null,
};

/* Human-readable labels for the fields validated across the customer + pet forms. */
const FORM_FIELD_LABELS: Record<string, string> = {
  pet_name: "Pet Name",
  pet_species_type_id: "Species",
  pet_breed_id: "Breed",
  gender: "Gender",
  dob: "Date of Birth",
  weight_kg: "Weight",
  weight_gm: "Weight",
  pet_mood: "Pet Mood",
  name: "Customer Name",
  mobile: "Phone Number",
  alternate_number: "Alternate Mobile",
  email: "Email ID",
  gst_no: "GST Number",
  addresses: "Address",
};

type FormErrorSummaryItem = { key: string; message: string };

/* Turns react-hook-form's error object into a de-duplicated, user-facing list
   so we can show exactly which details are missing or invalid above the buttons. */
const buildFormErrorSummary = (errors: FieldErrors<IAddCustomerFormsFormFields>): FormErrorSummaryItem[] => {
  const summary: FormErrorSummaryItem[] = [];
  const seenLabels = new Set<string>();

  Object.entries(errors ?? {}).forEach(([fieldName, fieldError]) => {
    if (!fieldError) return;

    const label =
      FORM_FIELD_LABELS[fieldName] ??
      fieldName.replace(/_/g, " ").replace(/\b\w/g, (character) => character.toUpperCase());

    if (seenLabels.has(label)) return;
    seenLabels.add(label);

    const errorType = Array.isArray(fieldError) ? "required" : (fieldError as { type?: string })?.type ?? "";
    const message = errorType === "required" ? `${label} is required` : `${label} is missing or invalid`;
    summary.push({ key: fieldName, message });
  });

  return summary;
};

const appendExotelSessionCustomerPetIds = (customerId?: string | null, petId?: string | null) => {
  try {
    const rawExotelInfo = sessionStorage.getItem("exotel_information_current");
    if (!rawExotelInfo) return;

    const parsedExotelInfo = JSON.parse(rawExotelInfo) as IExotelInformationData;
    if (!parsedExotelInfo?.CRT) return;

    const updatedExotelInfo: IExotelInformationData = {
      ...parsedExotelInfo,
      customer_pet_data: {
        customer_id: customerId ?? parsedExotelInfo?.customer_pet_data?.customer_id ?? null,
        pet_id: petId ?? parsedExotelInfo?.customer_pet_data?.pet_id ?? null,
      },
    };

    sessionStorage.setItem("exotel_information_current", JSON.stringify(updatedExotelInfo));
    sessionStorage.setItem(`exotel_information:${updatedExotelInfo.CRT}`, JSON.stringify(updatedExotelInfo));
  } catch {
    // No-op: keep default add-customer flow unaffected.
  }
};

const AddCustomerForms: React.FC<IAddCustomerFormsProps> = ({ layoutFor, forUpdate, addressLayout, ...props }) => {
  /* Instantitions Starts */
  const { setInvisible } = useModalActions("BOOKING_MISSING_DETAILS_EDIT_MODAL");
  const currentDate = new Date(Date.now());
  const { convertWeightToKilogramGrams, addKilogramsAndGrams, convertDOBToYMDFormat } = useUtils();
  const selectedDate = currentDate.toISOString().slice(0, 10);
  const { getRouteKey } = useRouter();
  const [navTo, setNavTo] = useState<NavTo>("CP");
  const { getPetStatusCorrespondingBooleans } = useUtils();
  const { VMSSourceId } = useStringConstants();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const NAV_URL = navTo === "CP" ? getRouteKey("APPOINTMENTS_TAB", "url") : getRouteKey("AVAILABLE_SLOTS", "url");

  /* Instantiations Ends */

  /* API Starts */
  const { execute: updatePetAPI } = useCustomerPetUpdationAPI();
  const updateCustomerAPI = useCustomerUpdationAPI();

  const fetchCustomerDetails = useCustomerDetailsAPI();
  const fetchCustomerPetList = useCustomerPetListAPI();
  const { newUserId, execute: createCustomerAPI } = useCustomerCreationAPI();
  const { newPetId, execute: createCustomerPetAPI } = useCustomerPetCreationAPI();
  const { execute: addAddress } = useAddAddressAPI();
  /* API Ends */

  /* Redux States Starts */
  const { routeState } = useRouteInfo(getRouteKey("CUSTOMER_PROFILE", "id"));

  const { routeState: slotRoute } = useRouteInfo(ROUTES.ROOT.children.SLOTS.id);

  const { rescheduleFlag, rescheduleBookingIdForMissingInfo } = slotRoute.state as ISlotsState;

  const { petDetails, customerDetails, customerAddresses, customerId, petId, missingDetailsRedirect } =
    routeState.state as ICustomerProfileState;
  const { mappedClinicId } = useAppSelector((state) => state.root.UIStates);

  /* Redux States Ends */

  const formHook = useForm<IAddCustomerFormsFormFields>({
    mode: "onChange",
    defaultValues: {
      dob: null,
      addresses: [addressFieldDefaultValues],
      is_pet_active: true,
    },
  });

  const addressFieldActions = useFieldArray<IAddCustomerFormsFormFields>({
    control: formHook.control,
    name: "addresses",
  });

  /* Action Handlers Starts */

  const getExactPetStatus = (data: IAddCustomerFormsFormFields): string => {
    return data.is_pet_active
      ? "Active"
      : data.is_pet_deceased
        ? "Deceased"
        : data?.is_pet_inactive
          ? "inactive"
          : "inactive";
  };

  const constructCustomerCreationBody = (data: IAddCustomerFormsFormFields): ICustomerCreationRequestBody => {
    const address = data?.addresses[0];

    let addressField: ICustomerAddressBody = {
      address_1: address?.address_1 === "" ? null : address?.address_1,
      label: address?.label === "" ? null : address?.label,
    };

    // if (address?.address_2 || address?.address_2 !== "") {
    addressField = {
      ...addressField,
      address_2: address?.address_2 === "" ? null : address?.address_2,
      city: address?.city === "" ? null : address?.city,
      pincode: address?.pincode === "" ? null : address?.pincode,
      state: address?.state === "" ? null : address?.state,
      latitude: address?.latitude === "" ? null : address?.latitude,
      longitude: address?.longitude === "" ? null : address?.longitude,
      // };
    };
    return {
      address: addressField,
      alternate_number: data.alternate_number === "" ? null : data.alternate_number,
      email: data.email === "" ? null : data.email,
      is_whatsapp_alternate: data.is_whatsapp_2 ?? false,
      is_whatsapp_mobile: data.is_whatsapp_1,
      mobile: data.mobile,
      gst_no: data.gst_no.length === 0 ? null : data.gst_no,
      name: data.name,
      source_id: parseInt(VMSSourceId),
      clinic_id: mappedClinicId,
    };
  };

  const constructPetCreationBody = (
    data: IAddCustomerFormsFormFields,
    userId: string,
    withSource: boolean = true,
  ): ICustomerPetCreationBody => {
    const customerPetFormBody: ICustomerPetCreationBody = {
      pet_breed_id: data.pet_breed_id,
      pet_dob: data.dob,
      source_id: VMSSourceId,
      pet_status: getExactPetStatus(data),
      pet_gender:
        data.gender?.toLowerCase() === "male" ? "Male" : data.gender?.toLowerCase() === "female" ? "Female" : "Other",
      pet_species_type_id: data.pet_species_type_id,
      pet_mood: data.pet_mood,
      pet_name: data.pet_name,
      pet_weight: addKilogramsAndGrams(data.weight_kg, data.weight_gm),
      user_id: userId,
    };
    if (!withSource) {
      delete customerPetFormBody.source_id;
    }
    return customerPetFormBody;
  };

  const synthCustomerAddresses = (customerAddresesForUpdate: ICustomerAddressBody[]): ICustomerAddressBody[] => {
    // The backend rejects empty strings (e.g. `address.0.label: String should have at
    // least 1 character`), so send `null` for any blank field — matching the creation flow.
    const emptyToNull = (value?: string | null) => (value === "" ? null : value);
    return customerAddresesForUpdate?.map((ad) => {
      return {
        ...ad,
        address_1: emptyToNull(ad?.address_1),
        address_2: emptyToNull(ad?.address_2),
        label: emptyToNull(ad?.label),
        city: emptyToNull(ad?.city),
        state: emptyToNull(ad?.state),
        pincode: emptyToNull(ad?.pincode),
        receiver_name: emptyToNull(ad?.receiver_name),
        receiver_number: emptyToNull(ad?.receiver_number),
        latitude: emptyToNull(ad?.latitude),
        longitude: emptyToNull(ad?.longitude),
      };
    });
  };

  const constructCustomerUpdationBody = (data: IAddCustomerFormsFormFields): ICustomerUpdationRequestBody => {
    return {
      address: synthCustomerAddresses(data?.addresses)?.length === 0 ? null : synthCustomerAddresses(data?.addresses),
      email: data.email === "" ? null : data.email,
      alternate_number: data.alternate_number === "" ? null : data.alternate_number,
      is_whatsapp_alternate: data.is_whatsapp_2,
      is_whatsapp_mobile: data.is_whatsapp_1,
      mobile: data.mobile,
      gst_no: data.gst_no.length === 0 ? null : data.gst_no,
      name: data.name,
      source_id: parseInt(VMSSourceId),
      clinic_id: mappedClinicId,
    };
  };

  const constructPetUpdationBody = (data: IAddCustomerFormsFormFields, customerId: string) => {
    return constructPetCreationBody(data, customerId, false);
  };

  /* Action Handlers Ends */

  const onSubmit: SubmitHandler<IAddCustomerFormsFormFields> = async (data: IAddCustomerFormsFormFields) => {
    if (props?.addons) {
      const specialCase = props?.addons.case;

      if (specialCase === "BOOKING_MISSING_DETAILS") {
        /* When no pet exists and customer ID exists with no addresses registered */
        if (!petId && customerId && customerAddresses?.length === 0) {
          const addressBlockPayload = constructCustomerCreationBody(data)?.address;
          const petCreationPayload = constructPetCreationBody(data, customerId, true);
          const updateCustomerPayload = constructCustomerUpdationBody(data);

          addAddress(
            {
              ...addressBlockPayload,
              user_id: customerId,
            },
            {
              refetchCustomerDetails: true,
            },
          ).then((newAddressId) => {
            if (!newAddressId) return;
            /* Pick the new added address and apply it with current customer update payload */
            const selectedAddress = {
              ...updateCustomerPayload.address[0],
              address_1:
                updateCustomerPayload.address[0]?.address_1 === "" ? null : updateCustomerPayload.address[0]?.address_1,
              address_id: newAddressId,
            };
            const newCustomerUpdatePayload = {
              ...updateCustomerPayload,
              address: [selectedAddress],
            };
            updateCustomerAPI(customerId, newCustomerUpdatePayload).then((updateUserId: string) => {
              if (!updateUserId) return;
              createCustomerPetAPI(petCreationPayload, {
                setPetId: true,
                fetchAndSetPetDetails: true,
              }).then((createdPetId: string) => {
                if (!createdPetId) return;
                setInvisible();
                if (!missingDetailsRedirect) return;
                createdPetId && navigate(`${getRouteKey("AVAILABLE_SLOTS", "url")}/${updateUserId}/${createdPetId}`);
              });
            });
          });
          dispatch(setMissingDetailsRedirect(false));

          return;
        }
        /* No Pet exists with customer having multiple or 1 addresses */
        if (!petId && customerId && customerAddresses?.length > 0) {
          const createPetPayload = constructPetCreationBody(data, customerId, true);
          const updateCustomerPayload = constructCustomerUpdationBody(data);
          updateCustomerAPI(customerId, updateCustomerPayload).then((updatedCustomerId: string) => {
            if (!updatedCustomerId) return;
            createCustomerPetAPI(createPetPayload, {
              fetchAndSetPetDetails: true,
              setPetId: true,
            }).then((createdPetId: string) => {
              if (!createdPetId) return;
              setInvisible();
              if (!missingDetailsRedirect) return;
              navigate(`${getRouteKey("AVAILABLE_SLOTS", "url")}/${customerId}/${createdPetId}`);
            });
          });
          dispatch(setMissingDetailsRedirect(false));

          return;
        }
        /* Pet Exists and Customer Exists but having 0 address */
        if (petId && customerId && customerAddresses?.length === 0) {
          const addressBlockPayload = constructCustomerCreationBody(data)?.address;
          const petUpdatePayload = constructPetUpdationBody(data, customerId);
          const customerUpdatePayload = constructCustomerUpdationBody(data);
          addAddress(
            {
              ...addressBlockPayload,
              user_id: customerId,
            },
            {
              refetchCustomerDetails: true,
            },
          ).then((newAddressId) => {
            if (!newAddressId) return;
            const selectedAddress = {
              ...customerUpdatePayload.address[0],
              address_1:
                customerUpdatePayload.address[0]?.address_1 === "" ? null : customerUpdatePayload.address[0]?.address_1,
              address_id: newAddressId,
            };
            const newCustomerUpdatePayload = {
              ...customerUpdatePayload,
              address: [selectedAddress],
            };
            updateCustomerAPI(customerId, newCustomerUpdatePayload).then((updatedCustomerId: string) => {
              if (!updatedCustomerId) return;
              updatePetAPI(petUpdatePayload, customerId, petId).then((updatedPetId: string) => {
                if (!updatedPetId) return;
                setInvisible();
                if (!missingDetailsRedirect) {
                  // Staying on the profile — refresh customer + pet so the UI reflects the update.
                  fetchCustomerDetails(customerId);
                  fetchCustomerPetList(customerId, { petId });
                  return;
                }
                navigate(`${getRouteKey("AVAILABLE_SLOTS", "url")}/${customerId}/${petId}`);
              });
            });
          });

          dispatch(setMissingDetailsRedirect(false));
          return;
        }
        /* Pet exists and customer exists and having 1 or more addresses */
        if (petId && customerId && customerAddresses?.length >= 1) {
          const petUpdatePayload = constructPetUpdationBody(data, customerId);
          const customerUpdatePayload = constructCustomerUpdationBody(data);
          updateCustomerAPI(customerId, customerUpdatePayload).then((updatedCustomerId: string) => {
            // Customer update failed (e.g. 400) — keep the modal open, don't proceed.
            if (!updatedCustomerId) return;
            updatePetAPI(petUpdatePayload, customerId, petId).then((updatedPetId: string) => {
              if (!updatedPetId) return;
              setInvisible();
              if (!missingDetailsRedirect) {
                // Staying on the profile — refresh customer + pet so the UI reflects the update.
                fetchCustomerDetails(customerId);
                fetchCustomerPetList(customerId, { petId });
                return;
              }

              if (rescheduleFlag) {
                navigate(
                  `${getRouteKey(
                    "AVAILABLE_SLOTS",
                    "url",
                  )}/${customerId}/${petId}?type=reschedule&rbi=${rescheduleBookingIdForMissingInfo}`,
                );
              } else {
                navigate(`${getRouteKey("AVAILABLE_SLOTS", "url")}/${customerId}/${petId}`);
              }
            });
          });
          dispatch(setMissingDetailsRedirect(false));
          return;
        }
      }
    }
    /* Special Cases Ends */
    /* Pet Creation */

    if (layoutFor === "PET" && forUpdate === false) {
      const customerPetFormBody = constructPetCreationBody(data, customerId);
      createCustomerPetAPI(customerPetFormBody, {
        fetchAndSetPetDetails: true,
        navigateToAppointmentTab: true,
        setPetId: true,
      }).then((createdPetId) => {
        if (createdPetId) {
          localStorage.setItem("selectedPetId", createdPetId);
          navigate(`${getRouteKey("VACCINATION_TAB", "url")}/${customerId}/${createdPetId}`);
        }
      });
      return;
    }
    /* Customer Update */
    if (layoutFor === "CUSTOMER" && forUpdate === true) {
      const customerUpdationBody = constructCustomerUpdationBody(formHook.getValues());
      await updateCustomerAPI(customerId, customerUpdationBody).then((updatedCustomerId) => {
        // Only refresh on success — a failed (e.g. 400) update resolves null.
        if (!updatedCustomerId) return;
        fetchCustomerDetails(customerId);
        fetchCustomerPetList(customerId, { petId });
      });
      return;
    }

    /* Pet Update */
    if (layoutFor === "PET" && forUpdate === true) {
      if (customerDetails && petDetails && customerId) {
        const customerPetFormBody = constructPetUpdationBody(formHook.getValues(), customerId);
        const shouldFlagCartPetUpdate =
          props?.updatePetWeightViaCart === true || props?.addons?.case === "CART_PET_UPDATE";
        const updatePayload = shouldFlagCartPetUpdate
          ? { ...customerPetFormBody, update_pet_weight_via_cart: true }
          : customerPetFormBody;
        await updatePetAPI(updatePayload, customerId, petId)
          .then((updatedPetId) => {
            // A failed (e.g. 400) update resolves null — treat it as a failure.
            if (!updatedPetId) {
              if (props?.addons?.case === "CART_PET_UPDATE") {
                props.addons.onFailure?.();
              }
              return;
            }
            fetchCustomerDetails(customerId);
            fetchCustomerPetList(customerId, {
              petId: petId,
            });
            if (props?.addons?.case === "CART_PET_UPDATE") {
              props.addons.onSuccess?.();
            }
          })
          .catch(() => {
            if (props?.addons?.case === "CART_PET_UPDATE") {
              props.addons.onFailure?.();
            }
          });
      }
      return;
    }
    const customerCreationFormBody = constructCustomerCreationBody(data);

    formHook.trigger().then((isValid) => {
      if (isValid) {
        createCustomerAPI(customerCreationFormBody);
      }
    });
  };

  /* Page Effects Starts */

  /* Page Effects Ends */

  useEffect(() => {
    if (newUserId !== "" && newUserId !== null) {
      appendExotelSessionCustomerPetIds(newUserId, null);
      formHook.trigger().then((isValid) => {
        if (isValid) {
          const data = formHook.getValues();
          const customerPetFormBody: ICustomerPetCreationBody = constructPetCreationBody(data, newUserId);
          dispatch(
            setCustomerId({
              customerId: newUserId,
            }),
          );
          createCustomerPetAPI(customerPetFormBody);
        }
      });
    }
  }, [newUserId]);

  useEffect(() => {
    if (newUserId !== null && newUserId !== "" && newPetId !== null && newPetId !== "" && newPetId && newUserId) {
      appendExotelSessionCustomerPetIds(newUserId, newPetId);
      fetchCustomerDetails(newUserId);
      dispatch(
        setPetId({
          petId: newPetId,
        }),
      );
      fetchCustomerPetList(newUserId, {
        petId: newPetId,
      });
      // Navigate to customer profile screen when all data is set
      navigate(`${NAV_URL}/${newUserId}/${newPetId}`);
      return;
    }
  }, [newPetId]);

  const setCustomerFormDefaultValues = () => {
    const fields = ["name", "alternate_number", "is_whatsapp_1", "is_whatsapp_2", "email", "mobile"];
    fields.forEach((field: keyof IAddCustomerCustomerFormFields) => {
      formHook.setValue(field, customerDetails[field]);
    });
    const is_whatsapp_1 = customerDetails?.is_whatsapp_mobile;
    const is_whatsapp_2 = customerDetails?.is_whatsapp_alternate;
    const gst_no = customerDetails?.gst_no;
    if (gst_no) {
      formHook.setValue("gst_no", gst_no);
    }
    if (is_whatsapp_1 && is_whatsapp_2) {
      formHook.setValue("is_whatsapp_1", true);
      formHook.setValue("is_whatsapp_2", true);
    } else if (is_whatsapp_1) {
      formHook.setValue("is_whatsapp_1", true);
      formHook.setValue("is_whatsapp_2", false);
    } else if (is_whatsapp_2) {
      formHook.setValue("is_whatsapp_2", true);
      formHook.setValue("is_whatsapp_1", false);
    } else {
      formHook.setValue("is_whatsapp_1", false);
      formHook.setValue("is_whatsapp_2", false);
    }
  };

  const setPetFormDefaultValues = () => {
    const fields = ["pet_name", "dob", "pet_breed_id", "pet_species_type_id", "pet_mood", "gender"];
    fields.forEach((field: keyof IAddCustomerPetFormFields) => {
      if (field === "pet_name") {
        formHook.setValue("pet_name", petDetails?.name);
      } else {
        formHook.setValue(field, petDetails[field]);
      }
    });

    formHook.setValue("day", convertDOBToYMDFormat(petDetails?.dob).days);
    formHook.setValue("month", convertDOBToYMDFormat(petDetails?.dob).months);
    formHook.setValue("year", convertDOBToYMDFormat(petDetails?.dob).years);
    // Pet Wieight
    const petWeightUnits = convertWeightToKilogramGrams(petDetails?.weight);
    formHook.setValue("weight_kg", petWeightUnits?.kilograms);
    formHook.setValue("weight_gm", petWeightUnits?.grams);

    // setting pet statuses
    const petStatuses = getPetStatusCorrespondingBooleans(petDetails?.status ?? "inactive");
    Object.keys(petStatuses).forEach((statusKey: keyof IAddCustomerPetFormFields) => {
      formHook.setValue(statusKey, petStatuses[statusKey]);
    });
  };

  useEffect(() => {
    if (layoutFor === "BOTH" && customerDetails && forUpdate === true) {
      if (petDetails) {
        setPetFormDefaultValues();
      }
      setCustomerFormDefaultValues();
      if (props?.addons?.case === "BOOKING_MISSING_DETAILS") {
        formHook.trigger();
      }
    }

    if (layoutFor === "CUSTOMER" && customerDetails && forUpdate === true) {
      setCustomerFormDefaultValues();
    }
    if (layoutFor === "PET" && petDetails && forUpdate === true) {
      setPetFormDefaultValues();
    }
  }, [forUpdate, layoutFor, customerDetails, petDetails]);

  useEffect(() => {
    if (layoutFor === "BOTH" && props?.addons?.case === "BOOKING_MISSING_DETAILS" && forUpdate === true) {
      if (customerAddresses?.length === 0) {
        if (addressFieldActions?.fields?.length === 0) {
          addressFieldActions.append(addressFieldDefaultValues);
        }
      } else {
        formHook.setValue("addresses", customerAddresses);
      }
      return;
    }
    if (layoutFor === "CUSTOMER" && forUpdate === true) {
      formHook.setValue("addresses", customerAddresses);
      return;
    }
  }, [customerAddresses.length]);

  useEffect(() => {
    if (forUpdate) return;
    if (layoutFor !== "BOTH") return;
    if (typeof window === "undefined") return;

    const queryParams = new URLSearchParams(window.location.search);
    if (queryParams.get("isExotel") !== "true") return;
    const phoneNumber = queryParams.get("phone_number");
    if (phoneNumber === null) return;

    formHook.setValue("mobile", phoneNumber, { shouldValidate: true });
  }, [forUpdate, layoutFor, formHook]);

  /* Subscribing to `errors` + `submitCount` here (the useForm owner) makes the whole
     form subtree re-render on a submit attempt, so inline field errors turn red on
     Save — not only while typing. `submitCount` gates the summary to post-submit. */
  const { errors: formErrors, submitCount } = formHook.formState;
  const errorSummary = submitCount > 0 ? buildFormErrorSummary(formErrors) : [];

  return (
    <form onSubmit={formHook.handleSubmit(onSubmit)}>
      {layoutFor === "BOTH" && (
        <>
          <CustomerInfoFormComponent
            addressLayout={addressLayout}
            formHook={formHook}
            addressFieldActions={addressFieldActions}
            isForUpdate={forUpdate}
          />
          <PetInfoFormComponent formHook={formHook} isForUpdate={forUpdate} />
        </>
      )}
      {layoutFor === "CUSTOMER" && (
        <CustomerInfoFormComponent
          formHook={formHook}
          addressLayout={addressLayout}
          addressFieldActions={addressFieldActions}
          isForUpdate={forUpdate}
        />
      )}

      {layoutFor === "PET" && <PetInfoFormComponent formHook={formHook} isForUpdate={forUpdate} hideTitle={props.hideTitle} />}

      {errorSummary.length > 0 && (
        <div className="mt-6 rounded-md border border-red-300 bg-red-50 px-3 py-2" role="alert">
          <p className="text-xs font-semibold text-red-600">
            Please fix the following {errorSummary.length === 1 ? "detail" : `${errorSummary.length} details`} before
            saving:
          </p>
          <ul className="mt-1 list-disc pl-5">
            {errorSummary.map((item) => (
              <li key={item.key} className="text-[11px] text-red-500">
                {item.message}
              </li>
            ))}
          </ul>
        </div>
      )}

      <div className="flex justify-end items-center mt-6  right-0">
        <FooterAddCustomerComponent
          isFormForEdit={forUpdate}
          formHook={formHook}
          addons={props.addons}
          layoutFor={layoutFor}
          setNavTo={setNavTo}
          submitButtonTitle={
            forUpdate ? (props?.addons?.case === "BOOKING_MISSING_DETAILS" ? "Save" : "Update") : "Add & Save"
          }
          onSubmit={onSubmit}
        />
      </div>

      {forUpdate && layoutFor === "CUSTOMER" && (
        <AICustomerProfileCard customerId={customerId} />
      )}
      {forUpdate && layoutFor === "PET" && (
        <AIPetProfileCard userId={customerId} />
      )}
    </form>
  );
};

export default AddCustomerForms;
